<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <PrimeDialog
    :header="$t('importElement')"
    visible
    modal
    @keyup.esc="closePopup"
    class="popup"
  >
    <template #closeicon>
      <PrimeButton
        icon="ri-close-line"
        :aria-label="$t('close')"
        @click="closePopup"
        outlined
        text
        severity="secondary"
      />
    </template>
    <div v-if="importType === 'file'">
      <FileUpload
        name="upload_files"
        :chooseLabel="$t('imports.selectFiles')"
        :uploadLabel="$t('send')"
        :cancelLabel="$t('cancel')"
        @uploader="handleUpload"
        customUpload
        multiple
        :maxFileSize="2147483648"
        :disabled="isUploading"
        :accept="accept"
      >
        <template #empty
          ><p>{{ $t("dragAndDrop") }}</p></template
        >
      </FileUpload>
    </div>
    <div v-else-if="importType === 'folder'">
      <FolderUpload @close="closePopup" />
    </div>

    <template #footer>
      <PrimeButton
        :label="$t('close')"
        @click="closePopup"
        severity="secondary"
      />
    </template>
  </PrimeDialog>
</template>

<script>
import { mapState, mapActions } from "pinia";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import FileUpload from "primevue/fileupload";
import FolderUpload from "@/components/FolderUpload.vue";
import { useUploadStore } from "@/stores/upload.js";

export default {
  name: "ImportPopupWithWorker",
  components: {
    PrimeButton: Button,
    PrimeDialog: Dialog,
    FileUpload,
    FolderUpload,
  },
  props: {
    node: {
      type: Object,
      required: true,
    },
    importType: {
      type: String,
      default: "file",
    },
    forcedPlanName: {
      type: String,
      default: null,
    },
    accept: {
      type: String,
      default: null,
    },
  },
  emits: ["close", "upload-with-plan"],
  computed: {
    ...mapState(useUploadStore, ["isUploading"]),
  },
  methods: {
    ...mapActions(useUploadStore, ["startUpload"]),
    closePopup() {
      this.$emit("close", false);
    },

    handleUpload(event) {
      const proxyFiles = event.files;
      if (!proxyFiles || proxyFiles.length === 0) {
        return;
      }

      // If a plan is forced, emit event to parent to handle with UploadPlanPopup
      if (this.forcedPlanName) {
        const files = proxyFiles.map((proxyFile) => {
          return new File([proxyFile], proxyFile.name, {
            type: proxyFile.type,
          });
        });
        this.$emit("upload-with-plan", {
          files: files,
          planName: this.forcedPlanName,
        });
        this.closePopup();
        return;
      }

      const uploadItems = proxyFiles.map((proxyFile) => {
        const file = new File([proxyFile], proxyFile.name, {
          type: proxyFile.type,
        });

        return {
          file: file,
          relativePath: "",
        };
      });

      this.startUpload(uploadItems, this.node.id);
      this.closePopup();
    },
  },
};
</script>
